import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/Button';
import { Card, CardBody } from './ui/Card';
import { reportError } from '@/lib/errorReporter';

interface Props { children: ReactNode; fallback?: ReactNode; section?: string }
interface State { error: Error | null; reloading?: boolean }

/** Catches render-time crashes in a subtree so one bad section doesn't blank
 *  the whole panel. Logs to console + POSTs to /api/errors so the issue
 *  shows up in pm2 logs / admin activity_log automatically. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.section ? ` · ${this.props.section}` : ''}]`, error, info.componentStack);

    // Chunk-load failures = stale index.html in this tab referencing a chunk
    // the new deploy renamed. Reload to pick up the fresh index. Time-based
    // cooldown (3s) — short enough that consecutive panel clicks both recover,
    // long enough that a truly broken bundle doesn't reload-loop.
    const msg = error?.message || String(error);
    if (/Failed to fetch dynamically imported module|Loading chunk|Loading CSS chunk|Importing a module script failed/i.test(msg)) {
      try {
        const STAMP = 'telos:chunk-reload-at';
        const last = Number(sessionStorage.getItem(STAMP) || '0');
        const now = Date.now();
        if (!last || now - last > 3_000) {
          sessionStorage.setItem(STAMP, String(now));
          // Mark reloading so render() shows a spinner, not the error card,
          // for the brief moment before the reload completes.
          this.setState({ reloading: true });
          // eslint-disable-next-line no-console
          console.warn('[ErrorBoundary] stale chunk reference, auto-reloading:', msg);
          window.location.reload();
          return;
        }
      } catch { /* private mode etc — fall through to normal error UI */ }
    }

    reportError({
      message: msg,
      stack: (error.stack || '') + (info.componentStack ? '\n--- component stack ---' + info.componentStack : ''),
      section: this.props.section || 'panel',
    });
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.state.reloading) {
      // Auto-reload is firing — don't paint the error card.
      return (
        <div className="py-12 text-center text-2xs text-fg-subtle">Reloading…</div>
      );
    }
    if (this.props.fallback) return this.props.fallback;
    return (
      <Card>
        <CardBody className="py-8 text-center space-y-3">
          <AlertTriangle className="size-6 text-warn mx-auto" />
          <div className="text-sm font-semibold">Something went wrong in this section.</div>
          <div className="text-2xs text-fg-subtle font-mono max-w-md mx-auto break-all">
            {this.state.error.message}
          </div>
          <Button size="sm" variant="secondary" onClick={this.reset} className="mx-auto">
            <RefreshCw className="size-3.5" /> Retry
          </Button>
        </CardBody>
      </Card>
    );
  }
}
